/* AchievementGallery — Grid of unlocked / locked achievement badges.
 * Shown in the Profile page below the karma badge. Auto-refreshes from /api/achievements/me.
 *
 * Visual: 4-col grid of circular medals. Unlocked = vibrant gradient + glow + label.
 * Locked = muted greyscale + progress fraction. Tap a medal → modal with full description.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PawPrint, CheckCircle, Map, Users, Shield, Heart, Footprints, Sparkles, Lock, X,
} from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  "paw": PawPrint,
  "check-circle": CheckCircle,
  "map": Map,
  "users": Users,
  "shield": Shield,
  "heart": Heart,
  "footprints": Footprints,
  "sparkles": Sparkles,
};

export default function AchievementGallery() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null); // active achievement for modal

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/achievements/me");
        setData(data);
      } catch {/* silent */}
    })();
  }, []);

  if (!data) return null;
  const { achievements, unlocked_count, total } = data;

  return (
    <div data-testid="achievement-gallery" className="w-full">
      <div className="flex items-end justify-between mb-3 px-0.5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#9A9A9A]">
            Riconoscimenti
          </div>
          <div className="font-display text-[18px] sm:text-[20px] text-[#1A1A1A] mt-0.5">
            Le tue medaglie
          </div>
        </div>
        <div className="text-[12px] font-semibold text-[#FF7A1A] tabular-nums">
          {unlocked_count}/{total}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {achievements.map((a) => (
          <Medal key={a.id} ach={a} onClick={() => setOpen(a)} />
        ))}
      </div>

      <AnimatePresence>
        {open && <AchievementModal ach={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </div>
  );
}

function Medal({ ach, onClick }) {
  const Icon = ICONS[ach.icon] || Sparkles;
  const c = ach.color;
  const unlocked = ach.unlocked;
  const pct = Math.min(100, Math.round((ach.progress / ach.target) * 100));

  return (
    <button
      onClick={onClick}
      data-testid={`achievement-${ach.id}`}
      className="relative flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
    >
      <div
        className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-full flex items-center justify-center overflow-hidden"
        style={{
          background: unlocked
            ? `linear-gradient(135deg, ${c}, ${c}cc)`
            : "linear-gradient(135deg, #F0EBE3, #E5DFD3)",
          boxShadow: unlocked ? `0 6px 18px -6px ${c}90, 0 1px 3px rgba(0,0,0,0.05)` : "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {/* aura when unlocked */}
        {unlocked && (
          <motion.div
            aria-hidden
            className="absolute inset-0"
            animate={{ opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 2.6, repeat: Infinity }}
            style={{
              background: `radial-gradient(circle at 50% 30%, #FFFFFF55, transparent 70%)`,
            }}
          />
        )}
        <Icon
          className="h-6 w-6 sm:h-7 sm:w-7 relative z-10"
          color={unlocked ? "#FFFFFF" : "#B7AFA0"}
          strokeWidth={2}
        />
        {!unlocked && (
          <div className="absolute bottom-0.5 right-0.5 h-4 w-4 rounded-full bg-white border border-[#E5DFD3] flex items-center justify-center">
            <Lock className="h-2.5 w-2.5 text-[#9A9A9A]" />
          </div>
        )}
      </div>
      <div className="text-[10px] sm:text-[11px] font-semibold text-center leading-tight w-full px-0.5"
        style={{ color: unlocked ? "#1A1A1A" : "#9A9A9A" }}
      >
        {ach.label}
      </div>
      {!unlocked && (
        <div className="text-[9px] text-[#B7AFA0] tabular-nums leading-none -mt-0.5">
          {ach.progress}/{ach.target}
        </div>
      )}
    </button>
  );
}

function AchievementModal({ ach, onClose }) {
  const Icon = ICONS[ach.icon] || Sparkles;
  const c = ach.color;
  const unlocked = ach.unlocked;
  const pct = Math.min(100, Math.round((ach.progress / ach.target) * 100));
  const date = ach.unlocked_at ? new Date(ach.unlocked_at).toLocaleDateString("it-IT") : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center px-5"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="relative max-w-xs w-full bg-white rounded-3xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-[#FBF7F1] hover:bg-[#F0EBE3] flex items-center justify-center"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4 text-[#6B6B6B]" />
        </button>
        <div className="flex flex-col items-center text-center">
          <div
            className="h-20 w-20 rounded-full flex items-center justify-center relative overflow-hidden"
            style={{
              background: unlocked
                ? `linear-gradient(135deg, ${c}, ${c}cc)`
                : "linear-gradient(135deg, #F0EBE3, #E5DFD3)",
              boxShadow: unlocked ? `0 12px 32px -10px ${c}, 0 0 0 4px ${c}22` : "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {unlocked && (
              <motion.div
                aria-hidden
                className="absolute inset-0"
                animate={{ opacity: [0.4, 0.85, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity }}
                style={{ background: `radial-gradient(circle at 50% 30%, #FFFFFF77, transparent 70%)` }}
              />
            )}
            <Icon className="h-9 w-9 relative z-10" color={unlocked ? "#FFFFFF" : "#B7AFA0"} strokeWidth={2} />
          </div>
          <div className="mt-4 font-display text-[22px] leading-tight" style={{ color: unlocked ? c : "#6B6B6B" }}>
            {ach.label}
          </div>
          <p className="mt-2 text-[13px] text-[#6B6B6B] leading-snug">
            {ach.description}
          </p>
          {unlocked ? (
            <div className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FFE9D8] text-[12px] font-semibold text-[#FF7A1A]">
              <Sparkles className="h-3 w-3" /> Sbloccato {date && `il ${date}`}
            </div>
          ) : (
            <div className="mt-4 w-full">
              <div className="h-1.5 rounded-full bg-[#F0EBE3] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c }} />
              </div>
              <div className="mt-1.5 text-[11px] text-[#9A9A9A] tabular-nums">
                Progresso: {ach.progress}/{ach.target}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
